import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Description, Label, Select } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { BranchPickerOptions } from './BranchPickerOptions';
import {
    branchScopeLayout,
    isBranchScopeFullWidth,
    type BranchFilterOption,
    type BranchScopeCopy,
    type BranchScopeLayout,
} from './branchScopeMeta';
import { FieldIcon } from './FieldIcon';
import { FilterOptionsStatus } from './FilterOptionsStatus';
import { useBranchScopePicker } from './useBranchScopePicker';
import type { FilterOptionsState } from './useFilterOptionsState';

type BranchScopePickerProps = {
    options: FilterOptionsState<BranchFilterOption>;
    /** The branch the figures count; `null` is All branches. */
    value: string | null;
    onChange: (branchId: string | null) => void;
    copy: BranchScopeCopy;
    layout: BranchScopeLayout;
};

/**
 * An OrgAdmin's branch picker, whose stock the figures count: the list's
 * "Stock figures for" (bos-app#118) and the details page's "Stock for"
 * (bos-app#119). All branches is there before the branch list is, so the
 * picker always has its default; the list's loading or failure shows under
 * it. Its Select keys are its hook's to convert.
 */
export const BranchScopePicker: React.FC<BranchScopePickerProps> = ({
    options,
    value,
    onChange,
    copy,
    layout,
}) => {
    const { t } = useTranslation('pages');
    const { selectedKey, onSelectionChange } = useBranchScopePicker(
        value,
        onChange,
    );
    const placement = branchScopeLayout({ layout });

    return (
        <div className={placement.wrapper()}>
            <Select
                className={placement.field()}
                fullWidth={isBranchScopeFullWidth(layout)}
                onChange={onSelectionChange}
                value={selectedKey}
                {...getNodeTestIdProp('product-filters-branch')}
            >
                <Label>{copy.label}</Label>
                <Select.Trigger
                    className={placement.control({
                        className: 'items-center gap-2',
                    })}
                >
                    {layout === 'inline' ? (
                        <FieldIcon icon="solar:shop-2-linear" />
                    ) : null}
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <BranchPickerOptions
                        allBranchesLabel={t(
                            'products.components.productTable.filters.allBranches',
                        )}
                        branches={options.items}
                    />
                </Select.Popover>
                {copy.pickerDescription ? (
                    <Description>{copy.pickerDescription}</Description>
                ) : null}
            </Select>
            {options.status === 'ready' ? null : (
                <FilterOptionsStatus
                    onRetry={options.onRetry}
                    status={options.status}
                    subject="branch"
                />
            )}
        </div>
    );
};
